import { Box, Collapse, Icon, IconButton, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from '@mui/material'
import { observer } from 'mobx-react-lite'
import { Fragment, useState } from 'react'
import { AppButton } from 'src/component/common/analog/molecule/button/AppButton'
import { ModalWindow } from 'src/component/common/analog/template/ModalWindow'
import { AppSkeleton } from 'src/component/common/analog/molecule/AppSkelton'
import { useLocalizationStore } from 'src/feature/localization/store/localization.store'
import { iconMapping } from 'src/util/icon-mapping'
import { SetSkillDemands } from './SetSkillDemands'
import { AddUser } from './AddUser'
import { TeamRolesUsersList } from './TeamRolesUsersList'
import { useMyTeamStore } from '../../store/my-team.store'
import { RoleOverview } from '../../model/my-team-roles'
import { AppBox } from 'src/component/common/analog/molecule/AppBox'
import FlagIcon from '@mui/icons-material/Flag'
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import { TodoChecklist } from './TodoChecklist'
import { buildTodoChecklistItems, createTodoChecklistTemplates, getDisabledTodoActionTooltip } from './todo-checklist.helper'
import { ColorTokens } from 'src/feature/theme/model/Palette'

const rowDividerStyle = {
  '& > *': {
    borderBottom: '1px solid',
    borderBottomColor: 'grey.100',
  },
}

const EXPANDED_PANEL_PADDING = '14px'

export type TeamRolesTableProps = {
  roles: RoleOverview[]
  isLoading: boolean
  onRoleDataChanged?: () => Promise<void> | void
}

export const TeamRolesTable = observer(({ roles, isLoading, onRoleDataChanged }: TeamRolesTableProps) => {
  const { translation } = useLocalizationStore()
  const myTeamStore = useMyTeamStore()
  const [usersModalRoleId, setUsersModalRoleId] = useState<string | null>(null)
  const [selectedRoleId, setSelectedRoleId] = useState<string | null>(null)
  const [addUserRoleId, setAddUserRoleId] = useState<string | null>(null)
  const [expandedRoleId, setExpandedRoleId] = useState<string | null>(null)
  const [hasSkillDemandsChanges, setHasSkillDemandsChanges] = useState(false)
  const pageTranslation = translation.pages.myTeam
  const { metricsHeader, rolesTable, teamOverviewMetrics } = pageTranslation
  const disabledActionTooltip = getDisabledTodoActionTooltip(rolesTable.disabledActionTooltip())

  const getSkillRatingTooltip = (avgSkillRating?: number) => {
    if (typeof avgSkillRating !== 'number') {
      return ''
    }

    return teamOverviewMetrics.skillRatingSubtitle(avgSkillRating)
  }

  const handleOpenUsersModal = (roleId: string) => {
    setUsersModalRoleId(roleId)
  }

  const handleOpenSkillDemandsModal = (roleId: string) => {
    setHasSkillDemandsChanges(false)
    setSelectedRoleId(roleId)
  }

  const handleOpenAddUserModal = (roleId: string) => {
    setAddUserRoleId(roleId)
  }

  const handleCloseAddUserModal = async (reload: boolean) => {
    setAddUserRoleId(null)

    if (!reload) {
      return
    }

    await onRoleDataChanged?.()
  }

  const handleCloseSkillDemandsModal = async () => {
    setSelectedRoleId(null)

    if (!hasSkillDemandsChanges) {
      return
    }

    await onRoleDataChanged?.()
  }

  const handleToggleExpandedRole = (roleId: string) => {
    setExpandedRoleId(current => current === roleId ? null : roleId)
  }

  const formatDecimalMetric = (value?: number) => {
    return typeof value === 'number' ? value.toFixed(1) : '0.0'
  }

  const renderRatedSkillCell = (skill?: { name: string; avgSkillRating: number } | null) => {
    const tooltip = getSkillRatingTooltip(skill?.avgSkillRating)

    return (
      <Box sx={styles.skillCellContent}>
        {tooltip && (
          <Tooltip title={tooltip} placement='top' arrow>
            <Icon sx={styles.helpIcon} component={iconMapping.InfoIcon} />
          </Tooltip>
        )}
        <Box component='span'>{skill?.name || '-'}</Box>
      </Box>
    )
  }

  const renderExpandedMetric = (label: string, content: React.ReactNode) => (
    <AppBox sx={styles.expandedMetricItem}>
      <Typography sx={styles.expandedMetricLabel}>{label}</Typography>
      <Box>{content}</Box>
    </AppBox>
  )

  if (isLoading && roles.length === 0) {
    return (
      <AppSkeleton
        count={1}
        direction="column"
        rects={[
          { width: '100%', height: 40 },
          { width: '100%', height: 420 },
        ]}
      />
    )
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Table size="small" sx={{ width: '100%', tableLayout: 'fixed', minWidth: '100%' }}>
        <TableHead>
          <TableRow>
            <TableCell sx={styles.tableCell}>{rolesTable.name()}</TableCell>
            <TableCell sx={styles.tableCell}>{metricsHeader.expected()}</TableCell>
            <TableCell sx={styles.tableCell}>{metricsHeader.avgGap()}</TableCell>
            <TableCell sx={styles.tableCell}>{metricsHeader.coverage()}</TableCell>
            <TableCell sx={styles.actionsTableCell}>{rolesTable.actions()}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {roles.map((role) => {
            const skillGap = myTeamStore.teamRolesSkillGapMetricsOverview?.[role.id]
            const isExpanded = expandedRoleId === role.id
            const roleDetailsToggleLabel = isExpanded ? rolesTable.collapseRoleDetails() : rolesTable.expandRoleDetails()
            const allCompleted = skillGap?.allCompleted ?? false
            const checklistTemplates = createTodoChecklistTemplates({
              levelId: role.id,
              levelName: role.name,
              includeRoles: false,
              labels: {
                roles: {
                  completed: rolesTable.rolesTodoCompleted(),
                  pending: rolesTable.rolesTodoPending(),
                  action: rolesTable.rolesTodoAction(),
                },
                skillSet: {
                  completed: rolesTable.skillSetTodoCompleted(),
                  pending: rolesTable.skillSetTodoPending(),
                  action: rolesTable.skillSetTodoAction(),
                },
                expectedRating: {
                  completed: rolesTable.expectedRatingTodoCompleted(),
                  pending: rolesTable.expectedRatingTodoPending(),
                  action: rolesTable.expectedRatingTodoAction(),
                },
                users: {
                  completed: rolesTable.usersTodoCompleted(),
                  pending: rolesTable.usersTodoPending(),
                  action: rolesTable.usersTodoAction(),
                },
              },
            })
            const checklistItems = buildTodoChecklistItems({
              templates: checklistTemplates,
              completionMap: skillGap,
              allCompleted,
              overrides: {
                skillSetCompleted: {
                  actionLabel: rolesTable.roleSkillSetTodoAction(),
                  onAction: () => handleOpenSkillDemandsModal(role.id),
                },
                expectedRatingCompleted: {
                  actionLabel: rolesTable.roleExpectedRatingTodoAction(),
                  onAction: () => handleOpenSkillDemandsModal(role.id),
                },
                usersCompleted: {
                  actionLabel: rolesTable.roleUsersTodoAction(),
                  onAction: () => handleOpenAddUserModal(role.id),
                },
              },
            })
            const expandedMetrics = [
              { label: metricsHeader.totalUsers(), content: skillGap?.totalUsers ?? '-' },
              { label: metricsHeader.usedSkills(), content: skillGap?.usedSkills ?? '-' },
              { label: metricsHeader.highestRated(), content: renderRatedSkillCell(skillGap?.highestRatedSkill) },
              { label: metricsHeader.lowestRated(), content: renderRatedSkillCell(skillGap?.lowestRatedSkill) },
            ]

            return (
              <Fragment key={role.id}>
                <TableRow hover sx={styles.summaryRow}>
                  <TableCell sx={styles.tableCellBody}>
                    <AppBox alignItems='center' gap={1} sx={styles.roleNameCell}>
                      <Tooltip
                        title={roleDetailsToggleLabel}
                        placement='top'
                        arrow
                      >
                        <IconButton
                          aria-label={roleDetailsToggleLabel}
                          size='small'
                          onClick={() => handleToggleExpandedRole(role.id)}
                          sx={styles.expandButton}
                        >
                          {isExpanded ? <KeyboardArrowUpIcon fontSize='small' /> : <KeyboardArrowDownIcon fontSize='small' />}
                        </IconButton>
                      </Tooltip>
                      {!allCompleted && <Icon sx={styles.flagIcon} component={FlagIcon} />}
                      <Typography sx={styles.roleNameText}>{role.name}</Typography>
                    </AppBox>
                  </TableCell>
                  <TableCell sx={styles.tableCellBody}>{formatDecimalMetric(skillGap?.avgExpectedSkillRating)}</TableCell>
                  <TableCell sx={styles.tableCellBody}>{formatDecimalMetric(skillGap?.avgGap)}</TableCell>
                  <TableCell sx={styles.tableCellBody}>
                    <AppBox alignItems='center' gap={5}>
                      <Tooltip title={metricsHeader.coverageTooltip()} placement='top' arrow>
                        <Icon sx={styles.helpIcon} component={iconMapping.InfoIcon} />
                      </Tooltip>
                      <Typography sx={styles.expandedMetricValue}>{`${skillGap?.coverage ?? 0}%`}</Typography>
                    </AppBox>
                  </TableCell>
                  <TableCell sx={styles.tableCellBody}>
                    <Box sx={styles.actionsCell}>
                      <AppButton
                        fontSize={12}
                        variant='light'
                        textValue={metricsHeader.users()}
                        onClick={() => handleOpenUsersModal(role.id)}
                      />
                      <AppButton
                        fontSize={12}
                        variant='dark'
                        textValue={pageTranslation.setDemands()}
                        onClick={() => handleOpenSkillDemandsModal(role.id)}
                      />
                    </Box>
                  </TableCell>
                </TableRow>
                <TableRow hover>
                  <TableCell colSpan={5} sx={styles.expandedCell}>
                    <Collapse
                      in={isExpanded}
                      timeout={300}
                      easing={{ enter: 'ease-in-out', exit: 'ease-in-out' }}
                      mountOnEnter
                      unmountOnExit
                    >
                      <AppBox sx={styles.expandedContent}>
                        <AppBox sx={styles.expandedMetricsRow}>
                          {expandedMetrics.map(metric => (
                            <Fragment key={`${role.id}-${metric.label}`}>
                              {renderExpandedMetric(metric.label, metric.content)}
                            </Fragment>
                          ))}
                        </AppBox>
                        <AppBox sx={styles.todoSection}>
                          <TodoChecklist
                            title={rolesTable.todoListTitle()}
                            titleSx={styles.todoTitle}
                            listSx={styles.todoList}
                            items={checklistItems}
                            enforceSequentialActions
                            disabledActionTooltip={disabledActionTooltip}
                          />
                        </AppBox>
                      </AppBox>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </Fragment>
            )
          })}
        </TableBody>
      </Table>

      <ModalWindow
        modalProps={{
          open: Boolean(usersModalRoleId),
          onClose: () => setUsersModalRoleId(null),
          PaperProps: {
            sx: {
              maxWidth: { xs: '90%', md: '60%', lg: '35%' },
              borderRadius: '10px',
            },
          },
        }}
        titleProps={{
          textValue: metricsHeader.users(),
        }}
        subtitleProps={{
          textValue: rolesTable.usersModalSubtitle(),
        }}
        headerIcon={<GroupOutlinedIcon />}
      >
        {usersModalRoleId && (
          <AppBox sx={styles.usersModalContainer}>
            <TeamRolesUsersList
              levelId={usersModalRoleId}
              onAddUser={() => setAddUserRoleId(usersModalRoleId)}
            />
            <AppBox sx={styles.usersModalActions}>
              <AppButton
                fontSize={14}
                variant='contained'
                textValue={pageTranslation.inviteUserButton()}
                sx={styles.usersModalInviteButton}
                onClick={() => setAddUserRoleId(usersModalRoleId)}
              />
            </AppBox>
          </AppBox>
        )}
      </ModalWindow>

      {selectedRoleId && (
        <SetSkillDemands
          levelId={selectedRoleId}
          open={Boolean(selectedRoleId)}
          onClose={handleCloseSkillDemandsModal}
          onDataChanged={() => setHasSkillDemandsChanges(true)}
        />
      )}

      {addUserRoleId && (
        <AddUser
          show={true}
          levelId={addUserRoleId}
          onClose={handleCloseAddUserModal}
        />
      )}
    </Box>
  )
})

const styles = {
  tableCell: {
    color: 'text.secondary',
    verticalAlign: 'middle',
    whiteSpace: 'nowrap',
    fontSize: 12,
    fontWeight: 400,
  },
  tableCellBody: {
    verticalAlign: 'middle',
    whiteSpace: 'nowrap',
    fontWeight: 400,
  },
  actionsTableCell: {
    color: 'text.secondary',
    verticalAlign: 'middle',
    whiteSpace: 'nowrap',
    fontSize: 12,
    fontWeight: 400,
    width: '24%',
  },
  summaryRow: rowDividerStyle,
  roleNameCell: {
    minWidth: 0,
  },
  flagIcon: {
    height: 16,
    width: 16,
    color: 'error.main',
    flexShrink: 0,
  },
  roleNameText: {
    fontSize: 12,
    fontWeight: 400,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  expandButton: {
    marginLeft: -0.5,
    color: 'text.secondary',
  },
  actionsCell: {
    display: 'flex',
    gap: 5,
    flexWrap: 'nowrap',
    alignItems: 'center',
    width: 'max-content',
  },
  expandedCell: {
    padding: 0,
    backgroundColor: 'transparent',
    borderBottom: 0,
  },
  expandedContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    padding: EXPANDED_PANEL_PADDING,
    backgroundColor: ColorTokens.primary10,
  },
  expandedMetricsRow: {
    display: 'flex',
    alignItems: 'flex-start',
    flexWrap: { xs: 'wrap', lg: 'nowrap' },
    width: '100%',
    gap: '20px',
  },
  todoSection: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  todoList: {
    width: '100%',
  },
  todoTitle: {
    margin: 0,
    lineHeight: 1.2,
  },
  expandedMetricItem: {
    minWidth: 0,
    width: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    padding: 0,
    border: 0,
    borderRadius: 0,
    backgroundColor: 'transparent',
  },
  expandedMetricLabel: {
    fontSize: 11,
    color: 'text.secondary',
    fontWeight: 500,
  },
  expandedMetricValue: {
    fontSize: 12,
    color: 'text.primary',
  },
  skillCellContent: {
    display: 'flex',
    alignItems: 'center',
    gap: 5,
  },
  helpIcon: {
    height: 15,
    width: 15,
    color: 'secondary.main',
    cursor: 'help',
    flexShrink: 0,
  },
  usersModalContainer: {
    width: '100%',
    flexDirection: 'column',
    gap: 10,
  },
  usersModalActions: {
    width: '100%',
    justifyContent: 'center',
  },
  usersModalInviteButton: {
    width: '20em',
  },
}
